import { Booking, BookingDetails, ReadOnlyBooking, User } from 'hotdesk_typescript';
import { BookingStatus } from '@/hotdesk-ui/enum/booking-status';
import { hasRemaingTime } from '@/hotdesk-ui/utils/status';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';

/** PRD confirmed + provider capture (shown as “Confirmed” in UI) + completed. */
const CONFIRMED_REVIEW_STATUSES = new Set<BookingStatus>([
    BookingStatus.CONFIRMED_QUICK,
    BookingStatus.CONFIRMED_BY_HOST,
    BookingStatus.CONFIRMED_BY_SYSTEM,
    BookingStatus.PENDING_CAPTURE_MONEY,
    BookingStatus.COMPLETED,
]);

type BookingLike = Booking | ReadOnlyBooking;

export type ReviewableBookingContext = {
    currentUser?: User | null;
};

/** Same gate as checkout upgrade: prod is only www.hotdesk.com (never localhost). */
export const isProductionWeb = (): boolean => {
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (host === 'localhost' || host === '127.0.0.1') return false;
    }
    return process.env.NEXT_PUBLIC_SELF_URL === 'https://www.hotdesk.com/';
};

/**
 * Whether the Rate experience / add-review UI may show.
 * Local + QA: always on. Prod: both Firebase switches must be true.
 */
export async function loadWebReviewsAddEnabled(): Promise<boolean> {
    if (!isProductionWeb()) return true;

    try {
        const [featureOn, addOn] = await Promise.all([
            fetchRemoteConfig(
                RemoteConfigKeys.WEB_REVIEWS_FEATURE_SWITCH,
                'boolean'
            ),
            fetchRemoteConfig(RemoteConfigKeys.WEB_REVIEWS_ADD, 'boolean'),
        ]);
        return featureOn === true && addOn === true;
    } catch {
        return false;
    }
}

export function resolveIsHostUser(user: User | null | undefined): boolean {
    if (!user) return false;
    const meta = user.meta as Record<string, unknown> | undefined;
    if (meta?.is_host === true || meta?.is_host === 'true') return true;
    if (meta?.isHost === true || meta?.isHost === 'true') return true;
    if ((user as User & { isOwner?: boolean }).isOwner === true) return true;
    return user.isStaff === true;
}

/** No Show comes from the API: the host or an admin sets status 12. */
export function isBookingNoShow(status: BookingStatus): boolean {
    return status === BookingStatus.NO_SHOW;
}

/**
 * PRD: hosts cannot review bookings they made for themselves or on behalf of a guest.
 * Local / QA: skipped so hosts can test the Rate CTA on their listings.
 */
export function isHostBlockedFromReview(
    booking: BookingLike,
    currentUser: User | null | undefined
): boolean {
    // Local + QA: allow hosts to rate (including their own listings).
    if (!isProductionWeb()) return false;

    if (!currentUser?.id || !resolveIsHostUser(currentUser)) return false;

    // Host placed this booking for a guest.
    if (booking.guestUser) return true;

    // Host booked for themselves (booking owner is the host account).
    if (booking.user === currentUser.id) return true;

    return false;
}

/**
 * BE create uses get_object_or_404(Booking, pk=booking, user=request.user).
 * Only the booker can POST /rating/ — otherwise the API returns 404 Not found.
 */
export function isCurrentUserBooker(
    booking: BookingLike,
    currentUser: User | null | undefined
): boolean {
    if (currentUser?.id == null || booking.user == null) return false;
    return Number(booking.user) === Number(currentUser.id);
}

/**
 * PRD acceptance for reviewable booking:
 * 1. Current user is the booker (same ownership rule as POST /rating/)
 * 2. Status confirmed (Admin / Host / Quick) or completed
 * 3. End date+time reached
 * 4. Host check-in confirmed when check-in is required (ignored if flag is off)
 * 5. User is not a no-show
 * 6. Current user is not a host blocked from reviewing this booking (prod only)
 */
export function isBookingReviewable(
    booking: BookingLike | null | undefined,
    context?: ReviewableBookingContext
): boolean {
    if (!booking?.details) return false;

    // Must match BE ownership or submit returns {"detail":"Not found."}.
    if (!isCurrentUserBooker(booking, context?.currentUser)) return false;

    const status = booking.status as BookingStatus | undefined;
    if (status == null || !CONFIRMED_REVIEW_STATUSES.has(status)) return false;

    const details = booking.details as BookingDetails;

    if (hasRemaingTime(details.endDateTime, details.timezone)) return false;

    // Host check-in gate only applies when the booking requires check-in.
    // If isCheckinRequired is off, hostVerifiedCheckIn is ignored.
    const isCheckinRequired = details.isCheckinRequired ?? false;
    if (isCheckinRequired && !details.hostVerifiedCheckIn) return false;

    if (isBookingNoShow(status)) return false;

    if (isHostBlockedFromReview(booking, context?.currentUser)) return false;

    return true;
}

/** Dev-friendly reasons when Rate experience is hidden. */
export function getBookingReviewBlockReasons(
    booking: BookingLike | null | undefined,
    context?: ReviewableBookingContext
): string[] {
    if (!booking?.details) return ['missing_booking_details'];

    const reasons: string[] = [];
    const status = booking.status as BookingStatus | undefined;
    const details = booking.details as BookingDetails;

    if (!isCurrentUserBooker(booking, context?.currentUser)) {
        reasons.push('not_booking_owner');
    }
    if (status == null || !CONFIRMED_REVIEW_STATUSES.has(status)) {
        reasons.push(`status_not_reviewable:${status}`);
    }
    if (hasRemaingTime(details.endDateTime, details.timezone)) {
        reasons.push('end_date_time_not_reached');
    }
    const isCheckinRequired = details.isCheckinRequired ?? false;
    if (isCheckinRequired && !details.hostVerifiedCheckIn) {
        reasons.push('host_check_in_not_confirmed');
    }
    if (status != null && isBookingNoShow(status)) {
        reasons.push('marked_no_show');
    }
    if (isHostBlockedFromReview(booking, context?.currentUser)) {
        reasons.push('host_cannot_review');
    }

    return reasons;
}
